import { api } from './client';

// The payload carries raw counters only; every rate below is derived here, so
// there is exactly one definition of "CR" in the product.

export interface Bucket {
  clicks: number;
  uniq_clicks: number;
  regs: number;
  deps: number;
  payout_cents: number;
}

export interface BreakdownRow extends Bucket {
  key: string;
  label: string;
  sub: string | null;
  prev: Bucket;
}

export interface DailyPoint extends Bucket {
  date: string;
}

export interface TrackerDashboard {
  range: { from: string; to: string; days: number };
  prev_range: { from: string; to: string };
  totals: Bucket;
  prev_totals: Bucket;
  daily: DailyPoint[];
  prev_daily: DailyPoint[];
  by_app: BreakdownRow[];
  by_country: BreakdownRow[];
  by_campaign: BreakdownRow[];
  by_offer: BreakdownRow[];
  by_partner: BreakdownRow[];
  by_source: BreakdownRow[];
  app_country: BreakdownRow[];
  live_today: boolean;
  /** Oldest day the rollup holds. null = the cube has never been filled. */
  cube_start: string | null;
  generated_at: string;
}

export interface TrackerDashboardQuery {
  from: string;
  to: string;
  app_ids?: string[];
  countries?: string[];
  campaign_id?: string;
  offer_id?: string;
  partner?: string;
  source_type?: string;
  freelancer?: string;
  /** '1' Apple Search Ads only, '0' everything else. */
  asa?: string;
  label?: string;
}

export function getTrackerDashboard(q: TrackerDashboardQuery): Promise<TrackerDashboard> {
  const p = new URLSearchParams();
  p.set('from', q.from);
  p.set('to', q.to);
  if (q.app_ids?.length) p.set('app_ids', q.app_ids.join(','));
  if (q.campaign_id) p.set('campaign_id', q.campaign_id);
  if (q.offer_id)    p.set('offer_id', q.offer_id);
  if (q.partner)     p.set('partner', q.partner);
  if (q.source_type) p.set('source_type', q.source_type);
  if (q.freelancer)  p.set('freelancer', q.freelancer);
  if (q.asa)         p.set('asa', q.asa);
  if (q.label)       p.set('label', q.label);
  if (q.countries?.length) p.set('countries', q.countries.join(','));
  return api.get<TrackerDashboard>(`/admin/stats/tracker?${p.toString()}`);
}

// --- Play store listing ------------------------------------------------------

/** Google Play store-listing visitors and acquisitions (installs from the listing). */
export interface PlayListingBucket {
  visitors: number;
  acquisitions: number;
}

export interface PlayListing {
  range: { from: string; to: string };
  totals: PlayListingBucket;
  /** Keyed exactly like the tracker breakdown rows of the same tab. */
  by_app: Record<string, PlayListingBucket & { label: string; sub: string | null }>;
  by_country: Record<string, PlayListingBucket>;
  app_country: Record<string, PlayListingBucket>;
  /** Newest Play day in the window; null = nothing imported for it. */
  last_date: string | null;
}

/** Only the app and country filters exist on Play's side. */
export function getPlayListing(q: Pick<TrackerDashboardQuery, 'from' | 'to' | 'app_ids' | 'countries'>): Promise<PlayListing> {
  const p = new URLSearchParams();
  p.set('from', q.from);
  p.set('to', q.to);
  if (q.app_ids?.length) p.set('app_ids', q.app_ids.join(','));
  if (q.countries?.length) p.set('countries', q.countries.join(','));
  return api.get<PlayListing>(`/admin/stats/tracker/play?${p.toString()}`);
}

/** Listing visitors → installs, in percent. Denominator 0 ⇒ 0. */
export function playCr(b: PlayListingBucket): number {
  return b.visitors > 0 ? (b.acquisitions / b.visitors) * 100 : 0;
}

export function backfillTrackerCube(days: number): Promise<{ from: string; to: string; days: number; rows: number }> {
  return api.post(`/admin/stats/tracker/backfill?days=${days}`);
}

// --- derived metrics -------------------------------------------------------

/** Every rate the dashboard shows, from one bucket. Denominator 0 ⇒ 0. */
export function derive(b: Bucket) {
  const ratio = (n: number, d: number) => (d > 0 ? n / d : 0);
  return {
    /** Share of clicks that were the visitor's first in the uniqueness window. */
    uniq_rate: ratio(b.uniq_clicks, b.clicks) * 100,
    cr_reg: ratio(b.regs, b.clicks) * 100,
    cr_dep: ratio(b.deps, b.clicks) * 100,
    /** Registration → deposit. The step that separates cheap traffic from good traffic. */
    cr_reg_dep: ratio(b.deps, b.regs) * 100,
    /** Earnings per click, in dollars. */
    epc: ratio(b.payout_cents, b.clicks) / 100,
    /** Average advertiser payout per deposit, in dollars. */
    per_dep: ratio(b.payout_cents, b.deps) / 100,
    revenue: b.payout_cents / 100,
  };
}

export type MetricKey = 'clicks' | 'uniq_clicks' | 'regs' | 'deps' | 'revenue' | 'cr_dep' | 'epc';

export function metricValue(b: Bucket, key: MetricKey): number {
  switch (key) {
    case 'clicks':       return b.clicks;
    case 'uniq_clicks':  return b.uniq_clicks;
    case 'regs':         return b.regs;
    case 'deps':         return b.deps;
    case 'revenue':      return b.payout_cents / 100;
    case 'cr_dep':       return derive(b).cr_dep;
    case 'epc':          return derive(b).epc;
  }
}

/**
 * Percent change against the comparison period. Growth from zero has no
 * percentage — null makes the UI print "new" instead of a fake ∞%.
 */
export function delta(current: number, previous: number): number | null {
  if (previous === 0) return current === 0 ? 0 : null;
  return ((current - previous) / previous) * 100;
}
